import React, { useEffect, useMemo, useRef, useState } from "react"
import { Dropdown, Input } from "antd"
import { equals } from "ramda"
import { classNames } from "../../../utils/classNames"
import { filterSearchData, flattenData, isItemExpanded } from "./utils"
import { MultiSelectItemsList } from "./components/MultiSelectItemsList"
import { MultiSelectBottomButtons } from "./components/MultiSelectButtons"
import { MultiSelectTrigger } from "./components/MultiSelectTrigger"
import Search from "antd/lib/input/Search"

export enum MultiSelectSelectMode {
    Default = "default", // Every item is selectable, there is no special logic for parent/child items
    ItemsOnly = "items-only", // Only items without children are selectable, clicking parent item selects/deselects all children
}

export enum MultiSelectSearchMode {
    Default = "default", // When search matches an item name, show the matched items
    KeepChildren = "keep-children", // When search matches item with children, show all children too
}

export type MultiSelectItem<TValue = string> = {
    id: TValue
    label?: string
    labelOnly?: boolean
    suffix?: React.ReactNode // Right-aligned additional info
    renderCustomLabel?: (item: MultiSelectItem<TValue>, searchText: string) => React.ReactNode
    isInactive?: boolean
    children?: MultiSelectItem<TValue>[]
    className?: string
}

export type MultiSelectProps<TValue = string> = {
    buttonClassName?: string
    dropDownClassName?: string
    disabled?: boolean
    disableOnly?: boolean
    data: MultiSelectItem<TValue>[]
    showSearchThreshold?: number
    labels?: MultiSelectLabels<TValue>
    size?: "normal" | "large"
    selectMode?: MultiSelectSelectMode
    searchMode?: MultiSelectSearchMode
    keepSelectedOnSearch?: boolean
    searchPlaceholder?: string
    emptyMessage?: string
    collapseNested?: boolean
    value?: TValue[]
    onChange?: (selectedIds: TValue[]) => void
    // For overriding default item selection logic. Return new selectedIds array.
    onItemClickedOverride?: (
        item: MultiSelectItem<TValue>,
        isSelected: boolean,
        selectedIds: TValue[],
        onlyClicked: boolean
    ) => TValue[]
    // For rendering custom trigger element instead of default button
    renderTriggerElement?: (buttonProps: React.HTMLProps<HTMLButtonElement>) => React.ReactNode
    // For rendering custom action buttons instead of default "Apply" and "Select/Deselect All" buttons
    renderActionButtons?: (onApply: () => void, onCancel: () => void, selectedIds: string[]) => React.ReactNode
}

type MultiSelectLabels<TValue = string> = {
    allSelected: (count: number, selectedIds: TValue[]) => React.ReactNode | string
    noSelected: React.ReactNode | string
    countSelected: (count: number, selectedIds: TValue[]) => React.ReactNode | string
    singleSelected: (label: React.ReactNode | string) => React.ReactNode | string
}
export const MultiSelect = <TValue extends string>({
    size = "normal",
    disabled = false,
    disableOnly = false,
    showSearchThreshold = 10,
    collapseNested = false,
    selectMode = MultiSelectSelectMode.Default,
    searchMode = MultiSelectSearchMode.Default,
    keepSelectedOnSearch = false,
    searchPlaceholder = "Search...",
    emptyMessage = "No items found",
    labels = defaultLabelsProp,
    buttonClassName,
    dropDownClassName,
    data,
    value,
    onChange,
    onItemClickedOverride,
    renderTriggerElement,
    renderActionButtons,
}: MultiSelectProps<TValue>) => {
    const [isOpen, setOpen] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})

    const [selectedIds, setSelectedIds] = useState<TValue[]>(value ?? [])
    const [filteredData, setFilteredData] = useState(data)

    const flatData = useMemo(() => flattenData(data), [data])
    const filteredFlatData = useMemo(() => flattenData(filteredData), [filteredData])

    useEffect(() => {
        setSelectedIds(value ?? [])
    }, [value])

    useEffect(() => {
        setFilteredData(data)
    }, [data])

    const searchInputRef = useRef<Search>(null)

    useEffect(() => {
        if (flatData.length >= showSearchThreshold) {
            setTimeout(() => searchInputRef.current?.focus(), 100)
        }
    }, [flatData.length, isOpen, showSearchThreshold])

    const onVisibleChange = (isVisible: boolean) => {
        setOpen(isVisible)
        if (!isVisible) {
            onApply()
        }
    }

    const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.target.value ?? "").trim().toLowerCase()
        const filteredItems = filterSearchData(data, value, searchMode)

        setSearchValue(e.target.value)
        setFilteredData(filteredItems)

        if (!keepSelectedOnSearch) {
            setSelectedIds(filteredFlatData.filter(i => selectedIds.includes(i.id)).map(i => i.id))
        }
    }

    const onToggleExpand = (itemId: TValue) => {
        setExpanded(prev => {
            const isExpanded = isItemExpanded(prev, itemId, collapseNested, searchValue)
            return { ...prev, [itemId]: !isExpanded }
        })
    }

    const resetState = () => {
        setOpen(false)
        setSearchValue("")
        setExpanded({})
        setFilteredData(data)
    }

    const onApply = () => {
        resetState()
        if (onChange && !equals(selectedIds, value)) {
            onChange(selectedIds)
        }
    }

    const onCancel = () => {
        resetState()
        setSelectedIds(value ?? [])
    }

    const menu = (
        <div className={classNames(classes.menuWrap, dropDownClassName)}>
            {flatData.length >= showSearchThreshold && (
                <div className={classes.searchWrap}>
                    <Input.Search
                        allowClear={true}
                        placeholder={searchPlaceholder}
                        onChange={onSearch}
                        value={searchValue}
                        ref={searchInputRef}
                    />
                </div>
            )}

            <div className={classes.itemsWrap}>
                <MultiSelectItemsList
                    selectMode={selectMode}
                    searchMode={searchMode}
                    collapseNested={collapseNested}
                    filteredItems={filteredData}
                    selectedIds={selectedIds}
                    searchValue={searchValue}
                    expanded={expanded}
                    onToggleExpand={onToggleExpand}
                    setSelectedIds={setSelectedIds}
                    onItemClickedOverride={onItemClickedOverride}
                    disableOnly={disableOnly}
                    emptyMessage={emptyMessage}
                />
            </div>

            <MultiSelectBottomButtons
                selectMode={selectMode}
                filteredFlatData={filteredFlatData}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onApply={onApply}
                onCancel={onCancel}
                renderActionButtons={renderActionButtons}
            />
        </div>
    )

    return (
        <Dropdown
            disabled={disabled}
            onVisibleChange={onVisibleChange}
            visible={isOpen}
            overlay={menu}
            overlayClassName={classes.dropdownWrap}
            trigger={["click"]}>
            <MultiSelectTrigger
                flatData={flatData}
                selectedIds={selectedIds}
                labels={labels}
                size={size}
                buttonClassName={buttonClassName}
                renderTriggerElement={renderTriggerElement}
            />
        </Dropdown>
    )
}

const defaultLabelsProp: MultiSelectLabels<string> = {
    allSelected: () => "All items",
    noSelected: "No items selected",
    countSelected: count => `${count} items`,
    singleSelected: label => label,
}

const classes = {
    dropdownWrap: "w-full max-w-[280px]",
    menuWrap: "relative flex max-h-80 w-full max-w-md flex-col rounded border border-gray-100 bg-white py-2 shadow-md",
    itemsWrap: "overflow-x-hidden overflow-y-scroll pb-2",
    searchWrap: "sticky top-0 z-10 bg-white pt-0.5 pb-2 px-2",
}
